import React from "react";
import { useNavigate } from "react-router-dom";
import { Heart, Eye, ExternalLink, Github, Scale } from "lucide-react";
import type { InfraAgent } from "@/types";

interface AgentCardProps {
  agent: InfraAgent;
}

const AgentCard: React.FC<AgentCardProps> = ({ agent }) => {
  const navigate = useNavigate();

  const handleCardClick = (e: React.MouseEvent) => {
    // Check if the click was on an external link or its children
    const target = e.target as HTMLElement;
    if (target.closest('a[href^="http"]')) {
      // Don't navigate to agent detail if clicking on external links
      return;
    }
    // Navigate to agent detail page using React Router
    navigate(`/agent/${agent.id}`);
  };

  return (
    <div
      className="agent-card group relative overflow-hidden rounded-xl border-2 border-slate-200 dark:border-slate-700 shadow-lg transition-all hover:shadow-xl hover:-translate-y-2 hover:border-orange-300 dark:hover:border-orange-500 cursor-pointer"
      onClick={handleCardClick}
    >
      <div className="p-8">
        <div className="agent-card-content">
          {/* Title and Description */}
          <div className="block">
            <h3 className="agent-card-title text-2xl font-bold mb-4 group-hover:text-orange-500 dark:group-hover:text-orange-400 transition-colors leading-tight">
              {agent.title}
            </h3>
            <p className="agent-card-text text-base mb-6 leading-relaxed line-clamp-3">
              {agent.description}
            </p>
          </div>

          {/* Tags */}
          <div className="flex flex-wrap gap-2 mb-6">
            {agent.tags.slice(0, 3).map((tag) => (
              <span
                key={tag}
                className="agent-card-tag inline-flex items-center rounded-full px-3 py-1 text-sm font-medium"
              >
                {tag}
              </span>
            ))}
            {agent.tags.length > 3 && (
              <span className="agent-card-tag-more inline-flex items-center rounded-full px-3 py-1 text-sm font-medium">
                +{agent.tags.length - 3}
              </span>
            )}
          </div>
        </div>

        {/* Footer - Always at bottom */}
        <div className="agent-card-footer flex items-center justify-between">
          <div className="agent-card-meta flex items-center space-x-6 text-sm">
            <div className="flex items-center space-x-2">
              <Heart className="h-4 w-4" />
              <span className="font-medium">{agent.stats.likes}</span>
            </div>
            <div className="flex items-center space-x-2">
              <Eye className="h-4 w-4" />
              <span className="font-medium">{agent.stats.views}</span>
            </div>
            {((agent.license && agent.license !== "Unknown") ||
              (agent.metadata?.license &&
                agent.metadata.license !== "Unknown")) && (
              <div className="flex items-center space-x-2">
                <Scale className="h-4 w-4" />
                <span className="font-medium">
                  {agent.license || agent.metadata?.license}
                </span>
              </div>
            )}
          </div>

          <div className="flex items-center space-x-2">
            {agent.githubUrl && (
              <a
                href={agent.githubUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="agent-card-icon inline-flex items-center justify-center rounded-lg w-10 h-10 transition-colors hover:text-orange-500 dark:hover:text-orange-400"
              >
                <Github className="h-5 w-5" />
              </a>
            )}
            {agent.websiteUrl && (
              <a
                href={agent.websiteUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="agent-card-icon inline-flex items-center justify-center rounded-lg w-10 h-10 transition-colors hover:text-orange-500 dark:hover:text-orange-400"
              >
                <ExternalLink className="h-5 w-5" />
              </a>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default AgentCard;
